<template>
  <div
    class="file-viewer-video-wrapper"
    :class="{ seeking: isSeeking, waiting: isWaiting }"
    @mousemove="onMouseMove"
  >
    <video
      ref="player"
      :src="videoData.src"
      :muted="videoMuted"
      :autoPlay="autoPlay"
      preload
      v-on="$listeners"
      @progress="onProgress"
      @play="onPlay"
      @pause="onPause"
      @timeupdate="onTimeUpdate"
      @loadedmetadata="onLoadedMetadata"
      @seeking="isSeeking = true"
      @seeked="isSeeking = false"
      @error="isWaiting = false"
    ></video>
    <div
      v-if="viewFile.banner && showPoster"
      class="poster"
      :style="`background-image: url('${videoData.banner}')`"
      @click="play"
    ></div>
    <button v-show="isPaused" class="play-btn" @click="toggle">
      <i v-if="isPaused" class="iconfont icon-kaishibofang"></i>
      <i v-else class="iconfont icon-zantingbofang"></i>
    </button>
    <div class="loading-spinner"></div>
    <transition name="control-bar-slide-fade">
      <div
        v-show="currentTime > 0 && showControls"
        class="control-bar font-color-white"
      >
        <div class="left-controls">
          <div class="play-control" @click="toggle">
            <i v-if="isPaused" class="iconfont icon-kaishibofang"></i>
            <i v-else class="iconfont icon-zantingbofang"></i>
          </div>
          <volume-panel
            :value="volumeLevel"
            :muted.sync="videoMuted"
            @change="setVolume"
          ></volume-panel>
          <div class="current-time time-control">
            {{ currentTime | time(true) }}
          </div>
          <div class="time-control time-divider">/</div>
          <div class="duration time-control">{{ duration | time(true) }}</div>
        </div>
        <div class="right-controls">
          <span class="fullscreen-btn control-btn" @click="fullscreenToggle()">
            <i
              class="iconfont"
              :class="[isFullscreen ? 'icon-quanpingguanbi' : 'icon-quanping']"
            >
            </i>
            <div class="tooltip">
              {{ isFullscreen ? '退出' : '全屏' }}
            </div>
          </span>
        </div>
        <progress-control
          :load-progress="loadProgress"
          :play-progress="playProgress"
        ></progress-control>
      </div>
    </transition>
    <div v-if="currentTime > 0 && !showControls" class="control-bar collapsed">
      <progress-control
        :load-progress="loadProgress"
        :play-progress="playProgress"
        :player="this"
      ></progress-control>
    </div>
  </div>
</template>

<script>
import ProgressControl from './ProgressControl'
import VolumePanel from './VolumePanel'
import timeFilter from '@/utils/timeFilter'
import { THIRD_API } from '@/global/config'

// 视频清晰度顺序
const VIDEO_SHOW_MAP = {
  '1080p': {
    showList: 0, // 显示顺序
    playIndex: 3, // 默认播放数据
    res: 1080,
    label: '1080p 超清',
    selectedLabel: '超清'
  },
  '720p': {
    showIndex: 1,
    playIndex: 1,
    res: 720,
    label: '720p 高清',
    selectedLabel: '高清'
  },
  '480p': {
    showIndex: 2,
    playIndex: 0,
    res: 480,
    label: '480p 标清',
    selectedLabel: '标清'
  },
  '360p': {
    showIndex: 3,
    playIndex: 2,
    res: 360,
    label: '360p 流畅',
    selectedLabel: '流畅'
  }
}

export default {
  components: {
    ProgressControl,
    VolumePanel
  },
  filters: {
    time: timeFilter
  },
  props: {
    // src banner
    viewFile: {
      type: Object,
      default: () => ({})
    },
    fullscreen: Boolean,
    muted: Boolean,
    autoPlay: Boolean
  },
  data() {
    return {
      videoData: {},
      currentSrc: '',
      showPoster: true,
      isPaused: true,
      isSeeking: false,
      isWaiting: false,
      showControls: false,
      // 是否全屏
      isFullscreen: this.fullscreen,
      controlVisibilityTimer: null,
      volumeLevel: 0.5,
      buffered: 0,
      currentTime: 0,
      duration: 0,
      videoMuted: this.muted
    }
  },
  computed: {
    loadProgress() {
      if (this.duration) {
        return this.buffered / this.duration
      }
      return 0
    },
    playProgress() {
      if (this.duration) {
        return this.currentTime / this.duration
      }
      return 0
    }
  },
  mounted() {
    this.initVideo()
  },
  destroyed() {
    clearTimeout(this.controlVisibilityTimer)
  },
  methods: {
    async initVideo() {
      this.isWaiting = true
      if (this.viewFile.src) {
        this.videoData = this.viewFile
      } else {
        const token = this.viewFile['video_token']
        if (token) {
          const sortedVideo = await this.getWbVideo()
          this.videoData.src = sortedVideo[0].src
        }
      }
      this.isWaiting = false
    },
    async getWbVideo() {
      const params = { media_id: this.viewFile['video_token'] }
      const res = await this.$http.get(THIRD_API.video.link, { params })

      const {
        data: { list }
      } = res
      if (
        list &&
        Object.prototype.toString.call(list) === '[object Array]' &&
        list.length
      ) {
        const videoDetail = list[0].details
        const filterVideo = videoDetail
          .filter(item => {
            return VIDEO_SHOW_MAP[item.quality_label]
          })
          .map(item => {
            let params = {
              id: item.quality_label,
              src: item.url
            }
            params = Object.assign(
              {},
              VIDEO_SHOW_MAP[item.quality_label],
              params
            )
            return params
          })
        let sortVideo = filterVideo.sort((a, b) => {
          return a.playIndex - b.playIndex
        })
        return sortVideo
      }
    },
    onProgress() {
      const { player } = this.$refs
      if (player) {
        if (player.buffered.length > 0) {
          this.buffered = player.buffered.end(0)
        }
      }
    },
    onTimeUpdate() {
      const { player } = this.$refs
      if (player) {
        this.currentTime = player.currentTime
      }
    },
    onPlay() {
      this.showPoster = false
      this.isPaused = false
    },
    onPause() {
      this.isPaused = true
    },
    setVolume(value = this.volumeLevel) {
      const { player } = this.$refs
      if (player) {
        this.volumeLevel = value
        this.$refs.player.volume = value
      }
    },
    toggle() {
      if (this.isPaused) {
        this.play()
      } else {
        this.pause()
      }
    },
    play() {
      this.$refs.player.play()
    },
    pause() {
      this.$refs.player.pause()
    },
    setTime(time) {
      this.$refs.player.currentTime = time
    },
    onLoadedMetadata() {
      const { player } = this.$refs
      this.duration = player ? player.duration : 0
      this.setVolume()
      if (this.autoPlay) {
        player.play()
      }
    },
    fullscreenToggle() {
      this.isFullscreen = !this.isFullscreen
      this.$emit('fullscreen', this.isFullscreen)
    },
    onMouseMove() {
      this.showControls = true
      clearTimeout(this.controlVisibilityTimer)
      this.controlVisibilityTimer = setTimeout(() => {
        this.showControls = false
      }, 4000)
    }
  }
}
</script>

<style lang="scss" scoped>
.file-viewer-video-wrapper {
  position: relative;
  height: 100%;
  video {
    width: 100%;
    height: 100%;
    background-color: #000000;
    display: block;
    object-fit: contain;
  }
  .poster {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: #000000 no-repeat center/contain;
    cursor: pointer;
  }
  .play-btn {
    width: 80px;
    height: 80px;
    border-radius: 50px;
    position: absolute;
    top: 50%;
    left: 50%;
    margin-top: -40px;
    margin-left: -40px;
    outline: none;
    border: 2px solid #ffffff;
    background-color: rgba(0, 0, 0, 0.65);
    color: #ffffff;
    cursor: pointer;
    i {
      font-size: 50px;
    }
  }
  .control-bar {
    box-sizing: border-box;
    position: absolute;
    height: 64px;
    line-height: 48px;
    bottom: 0;
    left: 0;
    width: 100%;
    background-image: linear-gradient(
      to bottom,
      rgba(0, 0, 0, 0),
      rgba(0, 0, 0, 0.45)
    );
    padding: 16px 20px 0;
    user-select: none;
    .left-controls {
      float: left;
      height: 100%;
    }
    .right-controls {
      float: right;
      height: 100%;
    }
    .play-control {
      height: 100%;
      cursor: pointer;
      display: inline-block;
      overflow: hidden;
      margin-right: 16px;
      i {
        font-size: 32px;
      }
    }
    .time-control {
      display: inline-block;
      height: 100%;
      font-size: 12px;
      overflow: hidden;
    }
    .time-divider {
      margin: 0 2px;
    }
    &.collapsed {
      height: auto;
    }
  }
  .progress-control {
    position: absolute;
    top: 12px;
    left: 0;
    width: 100%;
    height: 4px;
    &:hover,
    &.dragging {
      top: 6px;
      height: 10px;
    }
  }
  .control-btn {
    margin-right: 24px;
    cursor: pointer;
    position: relative;
    &:hover {
      color: #3352fe;
      .tooltip {
        display: inline-block;
        color: #ffffff;
      }
    }
    &:last-child {
      margin-right: 0;
    }
    .tooltip {
      position: absolute;
      font-size: 12px;
      white-space: nowrap;
      background-color: #363636;
      top: 0;
      left: 50%;
      width: 48px;
      height: 20px;
      text-align: center;
      line-height: 20px;
      margin: -44px 0 0 -24px;
      border-radius: 3px;
      z-index: 10;
      display: none;
    }
    i {
      font-size: 20px;
    }
  }
  .loading-spinner {
    display: none;
    position: absolute;
    top: 50%;
    left: 50%;
    margin: -25px 0 0 -25px;
    opacity: 0.85;
    text-align: left;
    border: 6px solid rgba(43, 51, 63, 0.7);
    box-sizing: border-box;
    background-clip: padding-box;
    width: 50px;
    height: 50px;
    border-radius: 25px;
    visibility: hidden;
    &:before,
    &:after {
      content: '';
      position: absolute;
      margin: -6px;
      box-sizing: border-box;
      width: 50px;
      height: 50px;
      border-radius: 25px;
      opacity: 1;
      border: 6px solid transparent;
      border-top-color: #fff;
    }
  }
  &.seeking,
  &.waiting {
    .loading-spinner {
      display: block;
      animation: spinner-show 0s linear 0.3s forwards;
      &:before {
        animation: spinner-spin 1.1s cubic-bezier(0.6, 0.2, 0, 0.8) infinite,
          spinner-fade 1.1s linear infinite;
      }
      &:after {
        animation: spinner-spin 1.1s cubic-bezier(0.6, 0.2, 0, 0.8) 0.44s
            infinite,
          spinner-fade 1.1s linear 0.44s infinite;
      }
    }
  }
}
@keyframes spinner-show {
  to {
    visibility: visible;
  }
}
@keyframes spinner-spin {
  to {
    -webkit-transform: rotate(1turn);
    transform: rotate(1turn);
  }
}
@keyframes spinner-fade {
  0% {
    border-top-color: #73859f;
  }

  20% {
    border-top-color: #73859f;
  }

  35% {
    border-top-color: #fff;
  }

  60% {
    border-top-color: #73859f;
  }

  to {
    border-top-color: #73859f;
  }
}
.control-bar-slide-fade-enter-active {
  transition: all 0.3s ease;
}
.control-bar-slide-fade-leave-active {
  transition: all 0.2s cubic-bezier(1, 0.5, 0.8, 1);
}
.control-bar-slide-fade-enter,
.control-bar-slide-fade-leave-to {
  transform: translateY(10px);
  opacity: 0;
}
</style>
